import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Star, Car, CalendarDays, ShieldCheck } from "lucide-react";

const stats = [
  { icon: CalendarDays, value: 5, suffix: "+", label: "Years in Business", context: "Serving Atlanta since 2019" },
  { icon: Car, value: 3000, suffix: "+", label: "Vehicles Serviced", context: "From daily drivers to exotics" },
  { icon: Star, value: 5.0, suffix: "", label: "Google Rating", decimals: 1, context: "Verified client reviews" },
  { icon: ShieldCheck, value: 100, suffix: "%", label: "Fully Insured & Bonded", context: "Your vehicle is protected" },
];

const certifications = [
  {
    name: "IDA Certified Detailer",
    logo: "https://cdn.ymaws.com/the-ida.site-ym.com/graphics/logo.png",
    alt: "International Detailing Association Certified Detailer",
    url: "https://the-ida.com/page/certification",
    featured: true,
  },
  {
    name: "Certified",
    logo: "https://thedingking.com/wp-content/uploads/2021/02/dk_certified_logo.png",
    alt: "Ding King Institute Certified",
    url: "https://thedingking.com",
  },
  {
    name: "Authorized Dealer",
    logo: "https://ceramiccoatinginc.com/cdn/shop/files/CERAMIC_COATING_INC_LOGO.png?v=1684942365&width=480",
    alt: "Ceramic Coating Inc Authorized Dealer",
    url: "https://ceramiccoatinginc.com",
  },
  {
    name: "Authorized Dealer",
    logo: "/certifications/suntek-logo.webp",
    alt: "SunTek Films Authorized Dealer",
    url: "https://suntekfilms.com/na/en/",
  },
  {
    name: "Authorized Dealer",
    logo: "https://geoshieldusa.com/cdn/shop/files/Geoshield_Wide_2157c9c1-df0c-4fa7-89cf-9851d18c1d61.svg?v=1761072920&width=400",
    alt: "GeoShield Window Authorized Dealer",
    url: "https://geoshieldusa.com",
  },
];

function AnimatedCounter({
  target,
  suffix,
  decimals = 0,
  active,
}: {
  target: number;
  suffix: string;
  decimals?: number;
  active: boolean;
}) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    const duration = 1800;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(current);
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [active, target]);

  return (
    <span className="text-3xl md:text-4xl font-display font-bold text-white">
      {decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}
      {suffix}
    </span>
  );
}

export default function TrustBar() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} className="py-16 bg-secondary/40 border-y border-border/50">
      <div className="container mx-auto px-4">
        {/* Stats as metric cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={inView ? { opacity: 1, scale: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.12, type: "spring", stiffness: 120 }}
              className="relative flex flex-col items-center text-center gap-2 p-6 rounded-2xl bg-card/50 border border-border/30 hover:border-primary/30 hover:shadow-[0_0_30px_-10px_hsl(var(--primary)/0.2)] transition-all duration-500"
            >
              {/* Subtle glow gradient at top */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent rounded-t-2xl" />

              <motion.div
                animate={inView ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.8, delay: 0.6 + i * 0.12 }}
              >
                <stat.icon className="h-6 w-6 text-primary mb-1" />
              </motion.div>
              <AnimatedCounter
                target={stat.value}
                suffix={stat.suffix}
                decimals={stat.decimals}
                active={inView}
              />
              <span className="text-foreground text-sm font-display tracking-wide uppercase">
                {stat.label}
              </span>
              <span className="text-muted-foreground text-xs">
                {stat.context}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Divider */}
        <div className="my-10 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        {/* Section heading */}
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center text-xs font-display uppercase tracking-[0.3em] text-foreground/80 mb-8"
        >
          Trusted & Certified
        </motion.h2>

        {/* Certifications */}
        <div className="flex items-center justify-center gap-8 flex-wrap">
          {certifications.map((cert, i) => (
            <motion.div
              key={cert.alt}
              initial={{ opacity: 0, y: 24, scale: 0.9 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.7 + i * 0.1 }}
              className="flex flex-col items-center gap-2"
            >
              <a
                href={cert.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-background/80 border border-border/30 rounded-xl px-8 py-4 transition-all duration-300 hover:opacity-90 hover:scale-105 hover:shadow-lg hover:shadow-primary/10"
              >
                <img
                  src={cert.logo}
                  alt={cert.alt}
                  width={160}
                  height={cert.featured ? 80 : 56}
                  className={`${cert.featured ? 'h-16 md:h-20' : 'h-12 md:h-14'} w-auto object-contain`}
                  loading="lazy"
                />
              </a>
              <span className="text-xs font-display uppercase tracking-[0.2em] text-muted-foreground/60">
                {cert.name}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
